import apiClient from './config';
import { Configuracion, PricingItem } from "../../types/index";

export const fetchConfiguraciones = async (): Promise<Configuracion[]> => {
    const response = await apiClient.get('/configurations');
    return response.data;
};

export const createConfiguracion = async (configData: Omit<Configuracion, '_id'>): Promise<Configuracion> => {
    const response = await apiClient.post('/configurations', configData);
    return response.data;
};

export const updateConfiguracion = async (id: string, configData: Configuracion): Promise<Configuracion> => {
    const response = await apiClient.put(`/configurations/${id}`, configData);
    return response.data.configuration;
};

export const deleteConfiguracion = async (id: string): Promise<void> => {
    await apiClient.delete(`/configurations/${id}`);
};

// ── Tarifas con nombre ──────────────────────────────────────────────────────
// Endpoints propios: cambiar un precio reprecia a los clientes que la tienen y
// borrar una tarifa en uso está bloqueado (409 TARIFA_EN_USO).

export interface TarifaNombradaInput {
    nombre: string;
    niveles: string[];
    precios: PricingItem[];
    cerradaAltas?: boolean;
}

export interface ReprecioTarifaResponse {
    dryRun: boolean;
    configuracion?: Configuracion;
    actualizadas: number;
    omitidasPrecioFijo: number;
    cambios: Array<{ id: string; cliente: string; frecuencia: string; antes: number; despues: number }>;
    errores?: Array<{ id: string; cliente?: string; motivo: string }>;
}

export const crearTarifaNombrada = async (configId: string, data: TarifaNombradaInput): Promise<Configuracion> => {
    const response = await apiClient.post(`/configurations/${configId}/tarifas-nombradas`, data);
    return response.data;
};

export const actualizarTarifaNombrada = async (
    configId: string,
    tarifaId: string,
    data: TarifaNombradaInput & { dryRun?: boolean }
): Promise<ReprecioTarifaResponse> => {
    const response = await apiClient.put(`/configurations/${configId}/tarifas-nombradas/${tarifaId}`, data);
    return response.data;
};

// Borrar una tarifa aunque tenga clientes: pasan a la otra tarifa de su nivel,
// a la general si no queda ninguna, o a `destino` si quedan varias. Vista
// previa por defecto; `bloqueado` si alguien se quedaría sin precio.
export interface BorradoTarifaResponse {
    dryRun: boolean;
    nombre: string;
    clientes: number;
    opcionesDestino: Array<{ _id: string; nombre: string }>;
    cambios: Array<{ id: string; cliente: string; antes: number; despues: number }>;
    sinCambios: number;
    omitidas: { precioFijo: number; ilimitada: number; sinPlazas: number };
    faltaElegir: Array<{ id: string; cliente: string }>;
    sinPrecio: Array<{ id: string; cliente: string; motivo: string }>;
    bloqueado: boolean;
    // Combinaciones que usan la tarifa: se borran con ella.
    combinacionesBorradas?: Array<{ _id: string; nombre: string }>;
}

export const eliminarTarifaConReasignacion = async (
    configId: string,
    tarifaId: string,
    params: { dryRun?: boolean; destino?: string | null }
): Promise<BorradoTarifaResponse> => {
    const response = await apiClient.post(`/configurations/${configId}/tarifas-nombradas/${tarifaId}/eliminar`, params);
    return response.data;
};

export const eliminarTarifaNombrada = async (configId: string, tarifaId: string): Promise<Configuracion> => {
    const response = await apiClient.delete(`/configurations/${configId}/tarifas-nombradas/${tarifaId}`);
    return response.data;
};

// ── Combinaciones de tarifas ────────────────────────────────────────────────
// Precio cerrado para quien mezcla tarifas ("Híbrido"). Modificar reprecia a
// sus clientes (con vista previa); borrar los devuelve a la suma.

export interface CombinacionInput {
    nombre: string;
    tarifas: string[];
    precios: Array<{ clases: number[]; price: number }>;
}

export type BorradoCombinacionResponse = Omit<BorradoTarifaResponse, 'opcionesDestino' | 'combinacionesBorradas'>;

export const crearCombinacion = async (configId: string, data: CombinacionInput): Promise<Configuracion> => {
    const response = await apiClient.post(`/configurations/${configId}/combinaciones`, data);
    return response.data;
};

export const actualizarCombinacion = async (
    configId: string,
    combinacionId: string,
    data: CombinacionInput & { dryRun?: boolean }
): Promise<ReprecioTarifaResponse> => {
    const response = await apiClient.put(`/configurations/${configId}/combinaciones/${combinacionId}`, data);
    return response.data;
};

export const eliminarCombinacion = async (
    configId: string,
    combinacionId: string,
    params: { dryRun?: boolean }
): Promise<BorradoCombinacionResponse> => {
    const response = await apiClient.post(`/configurations/${configId}/combinaciones/${combinacionId}/eliminar`, params);
    return response.data;
};

